/**
 * Premium Header Enhancement
 * Улучшения производительности и интерактивности для Premium Headers
 */

class PremiumHeaderEnhancer {
  constructor() {
    this.headers = new Map();
    this.observer = null;
    this.performanceObserver = null;
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
    this.setupPerformanceObserver();
    this.enhanceHeaders();
    this.setupReducedMotion();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: [0, 0.1, 0.5, 1]
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const header = entry.target;
        const headerId = header.id;

        if (entry.isIntersecting) {
          this.activateHeader(headerId);
        } else {
          this.deactivateHeader(headerId);
        }
      });
    }, options);
  }

  setupPerformanceObserver() {
    if ('PerformanceObserver' in window) {
      this.performanceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach(entry => {
          if (entry.entryType === 'measure' && entry.name.includes('premium-header')) {
            this.trackPerformance(entry);
          }
        });
      });

      this.performanceObserver.observe({ entryTypes: ['measure'] });
    }
  }

  enhanceHeaders() {
    const headers = document.querySelectorAll('.premium-header');

    headers.forEach(header => {
      const headerId = header.id || this.generateId();
      header.id = headerId;

      this.headers.set(headerId, {
        element: header,
        isActive: false,
        stats: this.extractStats(header),
        performance: {
          loadTime: 0,
          renderTime: 0
        }
      });

      this.observer.observe(header);
      this.optimizeHeader(headerId);
    });
  }

  optimizeHeader(headerId) {
    const headerData = this.headers.get(headerId);
    if (!headerData) return;

    const header = headerData.element;

    // Lazy load background effects
    this.lazyLoadEffects(header);

    // Optimize animations
    this.optimizeAnimations(header);

    // Setup hover effects
    this.setupHoverEffects(header);

    // Setup loading states
    this.setupLoadingStates(header);
  }

  lazyLoadEffects(header) {
    const background = header.querySelector('.header-background');
    if (!background) return;

    // Only load effects when header is visible
    const loadEffects = () => {
      background.style.opacity = '1';
      background.style.transition = 'opacity 0.5s ease';
    };

    // Check if header is in viewport
    const rect = header.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      loadEffects();
    } else {
      background.style.opacity = '0';
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            loadEffects();
            observer.unobserve(entry.target);
          }
        });
      });
      observer.observe(header);
    }
  }

  optimizeAnimations(header) {
    // Disable animations on low-end devices
    if (this.isLowEndDevice()) {
      header.classList.add('no-animations');
      return;
    }

    // Use CSS containment for better performance
    header.style.contain = 'layout style paint';

    // Optimize particle animations
    const particles = header.querySelector('.floating-particles');
    if (particles) {
      particles.style.willChange = 'transform';
    }

    // Optimize orb animations
    const orbs = header.querySelectorAll('.gradient-orb');
    orbs.forEach(orb => {
      orb.style.willChange = 'transform';
    });
  }

  setupHoverEffects(header) {
    const stats = header.querySelectorAll('.stat-item');

    stats.forEach(stat => {
      stat.addEventListener('mouseenter', () => {
        this.animateStat(stat, 'enter');
      });

      stat.addEventListener('mouseleave', () => {
        this.animateStat(stat, 'leave');
      });
    });
  }

  animateStat(stat, action) {
    const number = stat.querySelector('.stat-number');
    if (!number) return;

    if (action === 'enter') {
      number.style.transform = 'scale(1.1)';
      number.style.transition = 'transform 0.3s ease';
    } else {
      number.style.transform = 'scale(1)';
    }
  }

  setupLoadingStates(header) {
    const loading = header.querySelector('.header-loading');
    if (!loading) return;

    // Show loading initially
    loading.style.opacity = '1';

    // Hide loading when content is ready
    const hideLoading = () => {
      loading.style.opacity = '0';
      setTimeout(() => {
        loading.style.display = 'none';
      }, 300);
    };

    // Check if images are loaded
    const images = header.querySelectorAll('img');
    if (images.length === 0) {
      hideLoading();
    } else {
      let loadedCount = 0;
      images.forEach(img => {
        if (img.complete) {
          loadedCount++;
        } else {
          img.addEventListener('load', () => {
            loadedCount++;
            if (loadedCount === images.length) {
              hideLoading();
            }
          });
        }
      });
    }
  }

  setupReducedMotion() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.documentElement.classList.add('reduced-motion');
    }

    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
      if (e.matches) {
        document.documentElement.classList.add('reduced-motion');
      } else {
        document.documentElement.classList.remove('reduced-motion');
      }
    });
  }

  activateHeader(headerId) {
    const headerData = this.headers.get(headerId);
    if (!headerData || headerData.isActive) return;

    headerData.isActive = true;
    const header = headerData.element;

    // Add active class
    header.classList.add('active');

    // Start animations
    this.startAnimations(header);

    // Track performance
    this.measurePerformance(headerId, 'activate');
  }

  deactivateHeader(headerId) {
    const headerData = this.headers.get(headerId);
    if (!headerData || !headerData.isActive) return;

    headerData.isActive = false;
    const header = headerData.element;

    // Remove active class
    header.classList.remove('active');

    // Pause animations
    this.pauseAnimations(header);
  }

  startAnimations(header) {
    const particles = header.querySelector('.floating-particles');
    const orbs = header.querySelectorAll('.gradient-orb');

    if (particles) {
      particles.style.animationPlayState = 'running';
    }

    orbs.forEach(orb => {
      orb.style.animationPlayState = 'running';
    });
  }

  pauseAnimations(header) {
    const particles = header.querySelector('.floating-particles');
    const orbs = header.querySelectorAll('.gradient-orb');

    if (particles) {
      particles.style.animationPlayState = 'paused';
    }

    orbs.forEach(orb => {
      orb.style.animationPlayState = 'paused';
    });
  }

  measurePerformance(headerId, action) {
    if (!this.performanceObserver) return;

    const startTime = performance.now();

    requestAnimationFrame(() => {
      const endTime = performance.now();
      const duration = endTime - startTime;

      performance.mark(`premium-header-${action}-${headerId}`);
      performance.measure(
        `premium-header-${action}-${headerId}`,
        `premium-header-${action}-${headerId}`
      );
    });
  }

  trackPerformance(entry) {
    console.log(`Performance: ${entry.name} took ${entry.duration}ms`);
  }

  extractStats(header) {
    const stats = [];
    const statItems = header.querySelectorAll('.stat-item');

    statItems.forEach(item => {
      const number = item.querySelector('.stat-number')?.textContent;
      const label = item.querySelector('.stat-label')?.textContent;

      if (number) {
        stats.push({ number, label });
      }
    });

    return stats;
  }

  isLowEndDevice() {
    // Simple heuristic for low-end devices
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const memory = navigator.deviceMemory;

    return (
      (connection && connection.effectiveType === 'slow-2g') ||
      (memory && memory < 4) ||
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    );
  }

  generateId() {
    return 'premium-header-' + Math.random().toString(36).substr(2, 9);
  }

  // Public API
  getHeaderStats(headerId) {
    const headerData = this.headers.get(headerId);
    return headerData ? headerData.stats : null;
  }

  getPerformanceMetrics(headerId) {
    const headerData = this.headers.get(headerId);
    return headerData ? headerData.performance : null;
  }

  refreshHeaders() {
    this.headers.clear();
    this.enhanceHeaders();
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
    }

    this.headers.clear();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.premiumHeaderEnhancer = new PremiumHeaderEnhancer();
  });
} else {
  window.premiumHeaderEnhancer = new PremiumHeaderEnhancer();
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PremiumHeaderEnhancer;
}
